import React from "react";
import { Box } from "../../components/ui/box";
import { Button, type ButtonProps } from "../../components/ui/button";
import { Kbd, KbdGroup } from "../../components/ui/kbd";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

type ControlBarPresentation = "joined" | "dock";

const ControlBarPresentationContext =
  React.createContext<ControlBarPresentation>("joined");

/**
 * ControlBar — the floating control card that hosts a cluster of view tools
 * over a canvas surface (the map's zoom/locate controls, the automation
 * diagram's fit/zoom/orientation clusters): an elevated rounded card whose
 * children are seamless ghost buttons, usually joined through `Group` with
 * explicit separators.
 *
 * Two surface treatments:
 * - Default (themed): elevated surface fill + hairline border — for canvases
 *   that share the app's theme (the automation diagram pane).
 * - `backgroundColor` (raw override): surfaces the CSS theme does not paint —
 *   Google Maps tiles — pass the pre-resolved color. The joined treatment
 *   drops its hairline. A dock keeps its hairline around the padded island.
 *
 * `presentation="dock"` gives independent actions a padded, separated layout.
 * Use the default joined treatment for seamless groups with separators.
 */
type ControlBarProps = Omittable<{
  children: React.ReactNode;
  /**
   * Raw background color for non-themed surfaces (e.g. a map's control
   * fill). A joined bar omits the themed border.
   */
  backgroundColor?: string;
  /**
   * Raw hairline color, paired with `backgroundColor`, for canvases whose
   * ground the theme cannot know (map imagery): the caller resolves a line
   * legible against both the bar and the canvas. A joined bar draws it as a
   * ring outside its box so the bar's height stays exactly its content's; a
   * dock recolors the border it already has.
   */
  borderColor?: string;
  /** Minimum height in px (the map's touch-sized bars use 40). */
  minHeight?: number;
  /** Seamless joined controls, or separated controls in a padded dock. */
  presentation?: ControlBarPresentation;
  /** Layout axis for a dock. Joined bars keep their child-owned layout. */
  orientation?: "horizontal" | "vertical";
}>;

export function ControlBar({
  children,
  backgroundColor,
  borderColor,
  minHeight,
  presentation = "joined",
  orientation = "horizontal",
}: ControlBarProps) {
  const dock = presentation === "dock";
  const direction = orientation === "vertical" ? "column" : "row";
  const content = (
    <ControlBarPresentationContext.Provider value={presentation}>
      {children}
    </ControlBarPresentationContext.Provider>
  );

  // The card never clips. A joined action wears the card's own radius (read
  // from `--control-bar-radius`) and `Group` squares the corners it shares
  // with a neighbour, so a selected fill, its border, and the hover wash all
  // follow the card's curve on the outer actions, and a `CountSticker` riding
  // an action's corner can hang past the card edge unclipped. The themed card
  // has a 1px hairline, so its actions sit one pixel inside the outer curve.
  if (backgroundColor != null) {
    // A joined bar's raw hairline rides the shadow as a 1px ring: a real
    // border would grow the bar past its actions' height and break the
    // edge-for-edge band it shares with a raw-background input.
    const joinedRing =
      !dock && borderColor != null
        ? { boxShadow: `0 0 0 1px ${borderColor}, var(--popover-shadow)` }
        : undefined;
    return (
      <Box
        align="center"
        border={dock || undefined}
        borderColor={dock && borderColor == null ? "border" : undefined}
        className={dock ? undefined : "[--control-bar-radius:var(--radius-lg)]"}
        data-presentation={presentation}
        direction={dock ? direction : undefined}
        gap={dock ? "xs" : undefined}
        justify="center"
        minHeight={minHeight}
        padding={dock ? "xs" : undefined}
        rounded={dock ? "xl" : "lg"}
        // Every bar floating over a raw canvas shares the popover surface
        // shadow, joined and dock alike — matching `Input backgroundColor`.
        surfaceShadow="popover"
        // The canvas under the bar is imagery, not a themed surface, so both
        // colors arrive pre-resolved from the caller rather than as tokens.
        style={{
          backgroundColor,
          ...(dock && borderColor != null && { borderColor }),
          ...joinedRing,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      align="center"
      border
      borderColor="border"
      className={
        dock
          ? undefined
          : "[--control-bar-radius:calc(var(--radius-lg)-1px)]"
      }
      data-presentation={presentation}
      direction={dock ? direction : undefined}
      gap={dock ? "xs" : undefined}
      justify="center"
      minHeight={minHeight}
      // A floating cluster over the view it controls — its own surface.
      surface="popover"
      padding={dock ? "xs" : undefined}
      rounded={dock ? "xl" : "lg"}
    >
      {content}
    </Box>
  );
}

/**
 * ControlBarButton — one icon control inside a ControlBar, with a hover
 * tooltip naming the action (icon-only buttons never self-describe) and an
 * optional keycap hint beside the label. The joined treatment uses explicit
 * separators; each action takes the card's radius and `Group` squares the
 * shared corners, so washes run edge-to-edge between neighbours. The dock
 * treatment rounds each action inside the padded card. Both show selection.
 *
 * Composes as the `render` child of a popup trigger (a Menu or Popover
 * trigger) the way any button does: the trigger's ref and handlers pass
 * through to the button element and nothing wraps it, so the bar's joined
 * geometry holds. An action that opens a popup IS that popup's trigger — a
 * popup hung off the bar by `anchor` has none, and a menu without a trigger
 * cannot host submenus.
 */
type ControlBarButtonProps = Omittable<{
  /** The action's accessible name; also the tooltip text unless `tooltip`
   *  supplies its own copy. */
  label: string;
  /** Tooltip copy when it should read differently from the accessible name —
   *  a sentence naming the effect ("Quickly filter your projects") while the
   *  name stays the noun, or a name that carries a count the badge already
   *  shows. */
  tooltip?: string;
  /**
   * Keys shown as a keycap hint beside the label — e.g. `['1']`,
   * `['cmd', 'z']`. Purely informational; the handlers live with the caller.
   */
  shortcut?: readonly string[];
  /** Button size; the map's touch-sized controls use `icon-lg`. */
  size?: ButtonProps["size"];
  /** Marks a persistent toggle state and applies the selected treatment. */
  selected?: boolean;
  /** Tooltip placement relative to the action. */
  tooltipSide?: "top" | "right" | "bottom" | "left";
  /** The icon (an `<Icon/>`, colored by the caller when needed). A popup
   *  trigger rendering this action supplies it as its own children. */
  children?: React.ReactNode;
}> &
  /** Everything else is the leaf button's: `onClick`, `disabled`, and what a
   *  popup trigger rendering this action (`MenuTrigger render={<ControlBarButton
   *  />}`) merges down — its ref, press handlers and ARIA wiring. */
  Omit<
    ButtonProps,
    | "size"
    | "variant"
    | "shape"
    | "className"
    | "children"
    | "aria-label"
    | "aria-pressed"
  >;

export function ControlBarButton({
  label,
  tooltip,
  shortcut,
  size = "icon",
  selected,
  tooltipSide = "top",
  children,
  id,
  ...props
}: ControlBarButtonProps) {
  const presentation = React.useContext(ControlBarPresentationContext);

  return (
    <Tooltip>
      {/* A popup trigger rendering this action (the map's filter menu) stamps
          its own `id` and resolves the hovered trigger by that DOM id. That id
          lands HERE, on the TooltipTrigger, so the tooltip registers under the
          id the element carries; spread onto the Button it would win the merge
          and the bubble would never open. */}
      <TooltipTrigger
        id={id}
        render={
          <Button
            aria-label={label}
            aria-pressed={selected}
            // A joined action is a segment of the card: it wears the card's
            // radius (the Group squares shared corners) and, when selected,
            // drops the chip border — the tinted fill alone marks the
            // segment, since a border would survive only on the edges the
            // Group leaves open. Dock actions keep their own corners inside
            // the padded outer card.
            className={
              presentation === "joined"
                ? cn(
                    "rounded-(--control-bar-radius) before:rounded-(--control-bar-radius)",
                    selected === true && "border-transparent",
                  )
                : undefined
            }
            shape={presentation === "dock" ? "square" : undefined}
            size={size}
            color={selected === true ? "primary" : "neutral"}
            variant={selected === true ? "outline" : "ghost"}
            {...props}
          >
            {children}
          </Button>
        }
      />
      <TooltipPopup side={tooltipSide}>
        <Box align="center" direction="row" gap="xs">
          <Text size="xs">{tooltip ?? label}</Text>
          {shortcut?.length ? (
            <KbdGroup>
              {shortcut.map((key) => (
                <Kbd key={key}>{key}</Kbd>
              ))}
            </KbdGroup>
          ) : null}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}
